<template>
  <sui-form-field>
    <label>
      <small style="float: right; line-height: 2">Click on the action to change the local rules of the {{entity.target}}.</small>
      Rules
    </label>
    <sui-table :class="entity.target" size="small" compact celled>
      <sui-table-header>
        <sui-table-row>
          <sui-table-header-cell>Role / Member</sui-table-header-cell>
          <sui-table-header-cell :colspan="actions.length">
            <span class="reset" @click="resetRules()">reset</span>
            Actions
          </sui-table-header-cell>
        </sui-table-row>
      </sui-table-header>
      <sui-table-body v-if="rolesList.length">
        <template v-for="role in rolesList">
          <!-- Role -->
          <sui-table-row class="role main" :key="role.entity.id + 'main'" v-if="role.entity.id">
            <sui-table-cell class="role-name" :class="{clickable: role.entity.id}" :rowspan="isProjectRules ? 2 : 1" @click="role.entity.id ? $modals.globalAccess.open(role.entity) : () => {}">
              <i class="tag purple icon" /><b>{{role.entity.name}}</b>
            </sui-table-cell>
            <sui-table-cell textAlign="center" data-tooltip="Rules for project" v-if="isProjectRules">
              <i class="folder yellow fitted icon" />
            </sui-table-cell>
            <sui-table-cell class="clickable" :class="classByValue[role.actions[action].localRole]" v-for="action in mainActions" :key="action" :data-tooltip="role.actions[action].tooltip" @click="onToggle(role, action)">
              <i class="icon" :class="iconsByValue[role.actions[action].result]" />{{namesByAction[action]}}
            </sui-table-cell>
            <sui-table-cell :colspan="extraActions.length - mainActions.length" v-if="isProjectRules" />
          </sui-table-row>
          <sui-table-row class="role extra" :key="role.entity.id + 'extra'" v-if="role.entity.id && isProjectRules">
            <sui-table-cell textAlign="center" data-tooltip="Rules for project's services" v-if="isProjectRules">
              <i class="window maximize teal fitted icon" />
            </sui-table-cell>
            <sui-table-cell class="clickable" :class="classByValue[role.actions[action].localRole]" v-for="action in extraActions" :key="action" :data-tooltip="role.actions[action].tooltip" @click="onToggle(role, action)">
              <i class="icon" :class="iconsByValue[role.actions[action].result]" />{{namesByAction[action]}}
            </sui-table-cell>
          </sui-table-row>
          <!-- Members -->
          <template v-if="role.members.length">
            <template v-for="member in role.members">
              <sui-table-row class="member main" :key="member.entity.id + 'main'">
                <sui-table-cell class="member-name" :class="{clickable: member.entity.id}" :rowspan="isProjectRules ? 2 : 1" @click="member.entity.id ? $modals.globalAccess.open(member.entity) : () => {}">
                  <i class="angle double right icon" v-show="role.entity.id" />
                  <i class="user blue icon" />{{member.entity.name}}
                </sui-table-cell>
                <sui-table-cell textAlign="center" data-tooltip="Rules for project" v-if="isProjectRules">
                  <i class="folder yellow fitted icon" />
                </sui-table-cell>
                <sui-table-cell class="clickable" :class="classByValue[member.actions[action].localMember]" v-for="action in mainActions" :key="action" :data-tooltip="member.actions[action].tooltip" @click="onToggle(member, action)">
                  <i class="icon" :class="iconsByValue[member.actions[action].result]" />{{namesByAction[action]}}
                </sui-table-cell>
                <sui-table-cell :colspan="extraActions.length - mainActions.length" v-if="isProjectRules" />
              </sui-table-row>
              <template v-if="isProjectRules">
                <sui-table-row class="member extra" :key="member.entity.id + 'extra'">
                  <sui-table-cell textAlign="center" data-tooltip="Rules for project's services" v-if="isProjectRules">
                    <i class="window maximize teal fitted icon" />
                  </sui-table-cell>
                  <sui-table-cell class="clickable" :class="classByValue[member.actions[action].localMember]" v-for="action in extraActions" :key="action" :data-tooltip="member.actions[action].tooltip" @click="onToggle(member, action)">
                    <i class="icon" :class="iconsByValue[member.actions[action].result]" />{{namesByAction[action]}}
                  </sui-table-cell>
                </sui-table-row>
              </template>
            </template>
          </template>
        </template>
      </sui-table-body>
      <sui-table-body v-if="!rolesList.length">
        <sui-table-row>
          <sui-table-cell :colspan="actions.length + 1">
            There are no <router-link to="/account/roles" @click.native="$modals.entityAccess.close()">roles</router-link> or <router-link to="/account/members" @click.native="$modals.entityAccess.close()">members</router-link>.
          </sui-table-cell>
        </sui-table-row>
      </sui-table-body>
    </sui-table>
  </sui-form-field>
</template>

<script>
  import {mapGetters} from 'vuex'
  import merge from 'lodash/merge'
  import FormAccess from '@/mixins/FormAccess'
  import FormField from '@/mixins/FormField'
  import objects from '@/objects'

  export default {
    name: 'FormEntityRules',
    mixins: [
      FormAccess,
      FormField,
    ],
    computed: {
      ...mapGetters(['members', 'membersById', 'projectsById', 'roles', 'rolesById']),
      actions() {
        return this.rulesByGroup[this.entity.target]
      },
      isProjectRules() {
        return this.entity.target === 'project'
      },
      rolesList() {
        const rolesList = []
        const entity = this.$store.getters[`${this.entity.target}sById`][this.entity.id]
        const nullRole = objects.Role.create({id: null, name: 'Without role'})
        for (const role of this.roles.concat(nullRole)) {
          const roleRules = {entity: role, actions: {}, members: []}
          const globalRoleRules = entity.rules.global && entity.rules.global[role.id] || {actions: {}}
          const projectRoleRules = entity.rules.project && entity.rules.project[role.id] || {actions: {}}
          const localRoleRules = entity.rules.local && this.entity.rules.local[role.id] || {actions: {}}
          for (const action of this.actions) {
            const globalRole = globalRoleRules.actions[action]
            const projectRole = projectRoleRules.actions[action]
            const localRole = localRoleRules.actions[action]
            roleRules.actions[action] = {
              globalRole,
              projectRole,
              localRole,
              ...merge({result: globalRole}, {result: projectRole}, {result: localRole}),
            }
            roleRules.actions[action].tooltip = this.getActionTooltip(roleRules, action)
          }
          roleRules.members = role._members.filter(member => !member.isOwner).map(member => {
            const memberRules = {entity: member, actions: {}}
            const globalMemberRules = entity.rules.global && entity.rules.global[member.id] || {actions: {}}
            const projectMemberRules = entity.rules.project && entity.rules.project[member.id] || {actions: {}}
            const localMemberRules = this.entity.rules.local && this.entity.rules.local[member.id] || {actions: {}}
            for (const action of this.actions) {
              const globalRole = globalRoleRules.actions[action]
              const globalMember = globalMemberRules.actions[action]
              const projectRole = projectRoleRules.actions[action]
              const projectMember = projectMemberRules.actions[action]
              const localRole = localRoleRules.actions[action]
              const localMember = localMemberRules.actions[action]
              memberRules.actions[action] = {
                globalRole,
                globalMember,
                projectRole,
                projectMember,
                localRole,
                localMember,
                ...merge({result: globalRole}, {result: globalMember}, {result: projectRole}, {result: projectMember}, {result: localRole}, {result: localMember}),
              }
              memberRules.actions[action].tooltip = this.getActionTooltip(memberRules, action)
            }
            return memberRules
          })
          rolesList.push(roleRules)
        }
        rolesList.sort(role => role.members.length ? -1 : 1).sort(role => role.entity.id ? 1 : -1)
        return rolesList
      },
      mainActions() {
        return this.isProjectRules ? this.actions.filter(action => action.includes('project')) : this.actions
      },
      extraActions() {
        return this.isProjectRules ? this.actions.filter(action => action.includes('service')) : []
      },
    },
    methods: {
      resetRules() {
        this.$attrs.value = {...this.$attrs.value, local: {}}
        this.onInput()
      },
      getActionTooltip(entityRules, action) {
        const symbolsByValue = {
          undefined: '⬜',
          0: '❌',
          1: '✅',
        }
        let tooltip = ''
        if (action.includes('project')) {
          tooltip += 'Project rules:\n'
        }
        if (action.includes('service')) {
          if (this.entity.target === 'project') {
            tooltip += 'Services in project:\n'
          } else {
            tooltip += 'Service rules:\n'
          }
        }
        if (action.includes('host')) {
          tooltip += 'Host rules:\n'
        }
        if (entityRules.entity.role !== null) {
          tooltip += `${symbolsByValue[entityRules.actions[action].globalRole]} Global role\n`
        }
        if (entityRules.entity.target === 'member') {
          tooltip += `${symbolsByValue[entityRules.actions[action].globalMember]} Global member\n`
        }
        if (this.entity.target === 'service') {
          if (entityRules.entity.role !== null) {
            tooltip += `${symbolsByValue[entityRules.actions[action].projectRole]} Project role\n`
          }
          if (entityRules.entity.target === 'member') {
            tooltip += `${symbolsByValue[entityRules.actions[action].projectMember]} Project member\n`
          }
        }
        if (entityRules.entity.role !== null) {
          tooltip += `${symbolsByValue[entityRules.actions[action].localRole]} Local role\n`
        }
        if (entityRules.entity.target === 'member') {
          tooltip += `${symbolsByValue[entityRules.actions[action].localMember]} Local member\n`
        }
        return tooltip
      },
      onToggle(data, action) {
        const rules = {...this.$attrs.value}
        const {globalRole, globalMember, projectRole, projectMember, localRole, localMember, result} = data.actions[action]
        const inheritedValue = merge({result: globalRole}, {result: globalMember}, {result: projectRole}, {result: projectMember}, {result: data.entity.target === 'member' ? localRole : undefined}).result
        const localValue = data.entity.target === 'role' ? localRole : localMember
        const hasInheritedValue = typeof inheritedValue !== 'undefined'
        const isSameRule = hasInheritedValue && (inheritedValue === localValue)
        if (rules.local[data.entity.id]) {
          if (isSameRule || (!hasInheritedValue && localValue === 0)) {
            delete rules.local[data.entity.id].actions[action]
          } else {
            rules.local[data.entity.id].actions[action] = result ? 0 : 1
          }
          if (!Object.keys(rules.local[data.entity.id].actions).length) {
            delete rules.local[data.entity.id]
          }
        } else {
          const actions = {}
          actions[action] = inheritedValue ? 0 : 1
          rules.local[data.entity.id] = {target: data.entity.target, actions}
        }
        this.$attrs.value = rules
        this.onInput()
      },
    },
  }
</script>

<style scoped>
  .table { margin: 0!important; white-space: nowrap; }
  .role { background-color: #f6f6f6; }
  .role-name, .member-name { padding-right: 3em!important; }
  th { line-height: 1; }
  td { padding: 0.5em!important; }
  .table.project td { padding: 0.3em 0.5em!important; }
  td.clickable { user-select: none; }
  td.clickable:hover { background: #eee; cursor: pointer; }
  td.notdefined { color: #999; }
  td.notdefined .icon { opacity: .5; }
  .extra td:first-child { border-left: 1px solid rgba(34, 36, 38, 0.1)!important; }
  small { color: #999; font-weight: normal; }
  .angle.icon { color: #ddd }
  .reset { float: right; color: #4183C4; }
  .reset:hover { color: #1e70bf; cursor: pointer; }
</style>
